import { yupResolver } from '@hookform/resolvers/yup';
import { RHFControl, RHFSelect } from 'components/common/form';
import {
  PAYMENT_OPTION_MONTHLY,
  PAYMENT_OPTION_ONE_OFF
} from 'components/donations/donation-packages/package-form/PackageForm';
import { locationTypesMap } from 'components/donations/donation-settings/Locations';
import AuthContext from 'context/authContext';
import { formatMoney } from 'helpers/utils';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo } from 'react';
import { Button, Card, Col, Row, Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useDonationTypes, useLocations, usePackages, useCurrencies } from 'hooks/useDonationSettings';

/* -------------------------------------------------------------------------- */
const donationDetailsScheme = Yup.object().shape({
  billingCondition: Yup.string().required('Required Field'),
  donationTypeId: Yup.string().required('Required Field'),
  locationIdList: Yup.array()
    .of(Yup.string())
    .min(1, 'Must select at least one location')
    .required('This field is required'),
  selectedPackage: Yup.string().required('Required Field'),
  donationAmount: Yup.string().required('Required Field'),
  customAmount: Yup.string().when('donationAmount', {
    is: 'custom',
    then: () =>
      Yup.number()
        .typeError('Please enter valid amount')
        .required('Required Field')
  })
});

const billingConditionsOptions = [
  {
    label: 'One-Off',
    value: PAYMENT_OPTION_ONE_OFF
  },
  {
    label: 'Monthly',
    value: PAYMENT_OPTION_MONTHLY
  }
];

const defaultValues = {
  billingCondition: PAYMENT_OPTION_ONE_OFF,
  donationTypeId: '',
  locationIdList: [],
  selectedPackage: '',
  donationAmount: '',
  customAmount: ''
};
/* -------------------------------------------------------------------------- */

const ADDonationDetails = ({ onSubmit, data }) => {
  const { organization, selectedBranchId } = useContext(AuthContext);

  const { donationTypes, isLoading: donationTypesLoading } = useDonationTypes();
  const { locations, isLoading: locationsLoading } = useLocations(locationTypesMap.project);
  const { currencies } = useCurrencies();

  // Watch form values to fetch packages dynamically
  const methods = useForm({
    defaultValues,
    resolver: yupResolver(donationDetailsScheme)
  });

  const { handleSubmit: RHFHandleSubmit, setValue, watch, reset } = methods;
  const {
    donationTypeId,
    billingCondition,
    selectedPackage,
    donationAmount,
    locationIdList
  } = watch();

  // Fetch packages based on selected filters
  const packageFilters = useMemo(() => ({
    paymentOption: billingCondition,
    donationTypeId: donationTypeId,
    locationIdList: locationIdList
  }), [billingCondition, donationTypeId, locationIdList]);

  const { packages, isLoading: packagesLoading } = usePackages(packageFilters);

  useEffect(() => {
    if (data) {
      reset(data);
    }
  }, [data, reset]);

  const donationTypesOptions = useMemo(
    () =>
      donationTypes?.map(({ id, donation_type_name }) => ({
        label: donation_type_name,
        value: id
      })) || [],
    [donationTypes]
  );
  const packagesOptions = useMemo(
    () =>
      packages?.map(({ id, package_title }) => ({
        label: package_title,
        value: id
      })) || [],
    [packages]
  );

  const locationsOptions = useMemo(() => {
    const options = locations?.map(({ id, location_name }) => ({
      label: location_name,
      value: id
    }));
    return [{ label: 'All', value: '*' }, ...(options || [])];
  }, [locations]);

  const donationAmountsOptions = useMemo(() => {
    const options = [];
    const selectedPackageData = packages?.find(
      ({ id}) => id === selectedPackage
    );
    if (selectedPackageData) {
      const { donation_amount } = selectedPackageData;
      donation_amount.forEach(donation_amount => {
        // const donationAmountData = donationAmountsData?.donation_amount?.find(
        //   ({ id }) => id === donation_amount
        // );
        if (donation_amount === 'custom') {
          options.push({
            label: 'Custom',
            value: 'custom'
          });
        } else {
          options.push({
            label: `${formatMoney(donation_amount)}`,
            value: donation_amount
          });
        }
      });
    }
    // TODO: Check if the package supports the custom amount
    return options;
  }, [packages, selectedPackage]);

  const handleSubmit = data => {
    let amount;
    if (data.donationAmount === 'custom') {
      amount = +data.customAmount;
    } else {
      amount = +data.donationAmount;
    }
    const donationType = {
      id: data.donationTypeId,
      name: donationTypes?.find(
        ({ id }) => id === data.donationTypeId
      )?.donation_type_name
    };
    const selectedPackageData = packages?.find(
      ({ id }) => id === data.selectedPackage
    );
    const name = selectedPackageData?.package_title;

    // Get currency info from package
    const currencyId = selectedPackageData?.currency_id;
    const currencyData = currencies?.find(c => c.id === currencyId);
    const currencySymbol = currencyData?.symbol || '£';

    // The donation's location(s) = the user's Location selection restricted to
    // the countries the chosen package actually covers. The Location select
    // defaults to "All" as a package FILTER, so intersecting keeps the stored
    // value meaningful: pick one country -> that country only; leave "All" ->
    // every country the package covers.
    const packageLocationIds = Array.isArray(selectedPackageData?.location)
      ? selectedPackageData.location
      : [];
    const intersection = (data.locationIdList || []).filter(locationId =>
      packageLocationIds.includes(locationId)
    );
    const donationLocationIds =
      intersection.length > 0 ? intersection : packageLocationIds;

    const locationNameList = donationLocationIds.map(locationId => {
      return locations?.find(({ id }) => id === locationId)?.location_name;
    });

    const submitData = {
      values: data,
      amount,
      type: donationType,
      name,
      packageId: data.selectedPackage,
      locations: locationNameList,
      donationLocationIds,
      currencySymbol,
      billingCondition: data.billingCondition
    };
    onSubmit(submitData);
    reset(defaultValues);
  };

  useEffect(() => {
    if (donationTypes && donationTypes.length > 0) {
      const generalType = donationTypes.find(
        type => type.donation_type_name.toLowerCase().includes('general')
      );
      setValue('donationTypeId', generalType?.id || '');
    }
    if (locations && locations.length > 0) {
      setValue(
        'locationIdList',
        locations?.map(location => location.id)
      );
    }
  }, [donationTypes, locations, setValue]);

  return (
    <Card className="mb-3">
      <Card.Header className="pb-0">
        <Card.Title className="fw-medium">
          Donation Details / Allocations
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <FormProvider {...methods}>
          <Row className="g-3">
            <Col sm={12} md={6}>
              <RHFSelect
                label="Billing Conditions"
                name={'billingCondition'}
                options={billingConditionsOptions}
                required
              />
            </Col>
            <Col sm={12} md={6}>
              <RHFSelect
                label="Donation Type"
                name={'donationTypeId'}
                isLoading={donationTypesLoading}
                options={donationTypesOptions}
                required
              />
            </Col>
            <Col sm={12} md={6}>
              <RHFSelect
                isMulti={true}
                allowSelectAll={true}
                allSelectedLabel="All"
                label="Location"
                name={'locationIdList'}
                isLoading={locationsLoading}
                options={locationsOptions}
                required
              />
            </Col>
            <Col sm={12}>
              <RHFSelect
                label="Current Appeals"
                name={'selectedPackage'}
                isLoading={packagesLoading}
                options={packagesOptions}
                required
              />
            </Col>
            {/* <Form.Group as={Col} md={12}>
            <Form.Label>Fund Available Appeals</Form.Label>
            <InputGroup>
              <InputGroup.Text
                id="basic-addon1"
                className="bg-transparent border-end-0 text-400"
              >
                <FontAwesomeIcon icon={'search'} />
              </InputGroup.Text>
              <FormControl
                className="border-start-0"
                placeholder="Search"
                aria-label="Search"
                aria-describedby="basic-addon1"
              />
            </InputGroup>
          </Form.Group> */}
            <Col sm={12}>
              <Form.Label>
                Amount <span className="text-danger">*</span>
              </Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {donationAmountsOptions.map(option => (
                  <Button
                    key={option.value}
                    variant={donationAmount === option.value ? 'primary' : 'outline-secondary'}
                    className="px-4 py-2"
                    onClick={() => setValue('donationAmount', option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </Col>
            {donationAmount === 'custom' && (
              <Col sm={12} md={6}>
                <RHFControl
                  label="Custom Amount"
                  placeholder="0.00"
                  name={'customAmount'}
                  required
                />
              </Col>
            )}

            {/* <Form.Group as={Col} md={3}>
            <Form.Label>Currency</Form.Label>
            <ReactSelect
              closeMenuOnSelect={true}
              options={donationCurrencyData}
              placeholder="Select"
              classNamePrefix="react-select"
              value={donationCurrency}
              onChange={value => setDonationCurrency(value)}
            />
            <Form.Control.Feedback type="invalid">
              Required
            </Form.Control.Feedback>
          </Form.Group> */}
          </Row>
          <Button
            size="lg"
            // type="submit"
            className="fs-0 fs-md-1 w-100 py-3 mt-4"
            onClick={RHFHandleSubmit(handleSubmit)}
          >
            {data ? 'Update' : 'Add Donation'}
          </Button>
        </FormProvider>
      </Card.Body>
    </Card>
  );
};

ADDonationDetails.propTypes = {
  onSubmit: PropTypes.func,
  data: PropTypes.object
};

export default ADDonationDetails;
